import React, { useState } from "react";
import { ReportAPI } from "../../api/apiClient";

interface Props {
  projectId: number;
}

interface DprRow {
  parentNode: number;
  levelNode: number;
  levelName: string;
  childNode: number;
  childNme: string;
  level: number;
  workGroupNo: number;
  workGroup: string;
  transaction: string;
  previous: number;
  achieved: number;
}

export default function DprCumulativeReport({ projectId }: Props) {
  const [selectedDate, setSelectedDate] = useState("");
  const [data, setData] = useState<DprRow[]>([]);
  const [loading, setLoading] = useState(false);

  const loadReport = async () => {
    if (!selectedDate) return;

    try {
      setLoading(true);

      // 🔥 IMPORTANT: pass negative projectId
      const result = await ReportAPI.getDprCumulative(
        -projectId,
        selectedDate,
        2
      );

      console.log("DPR Cumulative Result:", result);

      setData(result);
    } catch (err) {
      console.error(err);
      alert("Error loading report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">

      {/* Title */}
      <h2 className="text-xl font-semibold text-gray-800">
        DPR Cumulative Report
      </h2>

      {/* Date Picker */}
      <div className="flex items-center gap-3">
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="border px-3 py-2 rounded shadow-sm"
        />

        <button
          onClick={loadReport}
          className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 transition"
        >
          {loading ? "Loading..." : "Load"}
        </button>
      </div>

      {/* Report Cards */}
      <div className="space-y-4">

        {data.length > 0 ? (
          data.map((item) => {
            const prevPercent = Math.round(item.previous * 100);
            const achievedPercent = Math.round(item.achieved * 100);
            const total = Math.min(prevPercent + achievedPercent, 100);

            return (
              <div
                key={item.workGroupNo}
                className="bg-white rounded shadow border overflow-hidden"
              >
                {/* Blue Header */}
                <div className="bg-blue-800 text-white px-4 py-2 text-sm font-semibold">
                  CUMULATIVE PREVIOUS AND ON-DATE
                </div>

                <div className="p-4">

                  {/* Work Title */}
                  <div className="mb-3 text-gray-800 font-medium">
                    {item.levelName} &gt; {item.workGroup}
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded h-6 overflow-hidden flex">

                    {/* Previous */}
                    <div
                      className="bg-green-700 text-white text-xs flex items-center justify-center transition-all duration-700"
                      style={{ width: `${prevPercent}%` }}
                    >
                      {prevPercent > 0 && `${prevPercent}%`}
                    </div>

                    {/* Achieved */}
                    <div
                      className="bg-green-400 text-black text-xs flex items-center justify-center transition-all duration-700"
                      style={{ width: `${achievedPercent}%` }}
                    >
                      {achievedPercent > 0 && `${achievedPercent}%`}
                    </div>
                  </div>

                  {/* Total Percentage */}
                  <div className="mt-2 text-sm text-gray-600">
                    Total: {total}%
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          !loading && (
            <div className="text-gray-500">
              No data found for selected date.
            </div>
          )
        )}

      </div>
    </div>
  );
}
